import type { IEquipmentMaintenanceDraft, IEquipmentMaintenanceEntry } from '@d-orca/shared/equipment-maintenance';
import { authedFetch } from '@apis/auth';
import { parseStandardErrors } from './equipment';

/** A deadline as the schedule lists it, with who last did it. */
export interface IEquipmentMaintenanceListEntry extends IEquipmentMaintenanceEntry {
  lastDoneByName: string | null;
}

/** One completion in the asset's log. */
export interface IEquipmentMaintenanceRecord {
  title: string;
  dueDate: string;
  doneAt: string;
  doneByName: string;
}

function schedulePath(siteId: string, equipmentId: string, rest = ''): string {
  return `/api/sites/${encodeURIComponent(siteId)}/equipment/${encodeURIComponent(equipmentId)}/maintenance${rest}`;
}

const entryPath = (siteId: string, equipmentId: string, entryId: string) =>
  schedulePath(siteId, equipmentId, `/${encodeURIComponent(entryId)}`);

const json = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

async function send(path: string, init: RequestInit): Promise<void> {
  const res = await authedFetch(path, init);
  if (res.ok) return;
  return parseStandardErrors(res);
}

export async function fetchEquipmentMaintenance(
  siteId: string,
  equipmentId: string,
  signal?: AbortSignal,
): Promise<IEquipmentMaintenanceListEntry[]> {
  const res = await authedFetch(schedulePath(siteId, equipmentId), { signal });
  if (res.ok) return ((await res.json()) as { entries: IEquipmentMaintenanceListEntry[] }).entries;
  return parseStandardErrors(res);
}

/** Every completion on the asset, newest first. */
export async function fetchEquipmentMaintenanceLog(
  siteId: string,
  equipmentId: string,
  signal?: AbortSignal,
): Promise<IEquipmentMaintenanceRecord[]> {
  const res = await authedFetch(schedulePath(siteId, equipmentId, '/records'), { signal });
  if (res.ok) return ((await res.json()) as { records: IEquipmentMaintenanceRecord[] }).records;
  return parseStandardErrors(res);
}

export const createEquipmentMaintenance = (siteId: string, equipmentId: string, draft: IEquipmentMaintenanceDraft) =>
  send(schedulePath(siteId, equipmentId), { method: 'POST', ...json(draft) });

/** Renames a deadline; its dates and repeat can't be edited. */
export const updateEquipmentMaintenance = (siteId: string, equipmentId: string, entryId: string, title: string) =>
  send(entryPath(siteId, equipmentId, entryId), { method: 'PATCH', ...json({ title }) });

/** The deadline as it stands after being done: its next due date, or none for a one-time one. */
export async function markEquipmentMaintenanceDone(
  siteId: string,
  equipmentId: string,
  entryId: string,
): Promise<IEquipmentMaintenanceListEntry> {
  const res = await authedFetch(`${entryPath(siteId, equipmentId, entryId)}/done`, { method: 'POST' });
  if (res.ok) return (await res.json()) as IEquipmentMaintenanceListEntry;
  return parseStandardErrors(res);
}

export const deleteEquipmentMaintenance = (siteId: string, equipmentId: string, entryId: string) =>
  send(entryPath(siteId, equipmentId, entryId), { method: 'DELETE' });
